import React from "react";
import { types } from "mobx-state-tree";
import { GridColDef } from "@mui/x-data-grid-pro";
import { DetectionModel } from "../../models/DetectionModel";
import { translationService } from "../translation/TranslationService";
import { videoPlayerService } from "../video-player/VideoPlayerService";
import { GridColumnGroupingModel } from "@mui/x-data-grid-pro";

export type ViewType = "detected" | "extracted" | "combined";

export const SelectedView = {
  Detected: "detected" as ViewType,
  Extracted: "extracted" as ViewType,
  Combined: "combined" as ViewType,
};

function renderClickablecell(displayValue: string | number, timestamp : string) {
  const date = new Date(timestamp).getTime();
  const currentDate = new Date(new Date().toDateString()).getTime();
  const seconds = (date - currentDate) / 1000;
  return (
    <a
      href="#"
      style={{ cursor: "pointer", textDecoration: "underline" }}
      onClick={(event) => {
        event.preventDefault();
        videoPlayerService.jumpTo(seconds);
        const index = inspectionInfoStorage.detections.findIndex(
          (detection) => detection.timestamp === timestamp
        );
        if (index !== -1) {
          inspectionInfoStorage.setSelectedIndex(index);
        }
      }}
    >
      {displayValue}
    </a>
  );
}

const InspectionInfoStorageModel = types.model("InspectionInfoStorageModel", {
  detections: types.optional(types.array(DetectionModel), []),
  view: types.optional(types.enumeration<ViewType>("ViewType", [SelectedView.Detected, SelectedView.Extracted, SelectedView.Combined]), SelectedView.Detected),
  selectedIndex: types.maybeNull(types.number),

}).views((self) => ({
  get rows() {
    return self.detections.map((det, index) => ({
      id: `row-${index}`,
      ...det,
    }));
  },
  get combinedRows() {
    return self.detections.flatMap((det, index) => ([
      { id: `row-${index}-top`, part: "top", distance: det.distance, timestamp: det.timestamp, main_Code: det.text, c1: "", c2: "", q1: "", q2: "", postionFrom: "", positionTo: "" },
      { id: `row-${index}-bottom`, part: "bottom", distance: "", timestamp: "", text: "", main_Code: det.main_Code, c1: det.c1, c2: det.c2, q1: det.q1, q2: det.q2, postionFrom: det.postionFrom, positionTo: det.positionTo },
    ]));
  },
  get detectionColumns(): GridColDef[] {
    return [
      {
        field: "distance",
        headerName: translationService.translate("ocrService.inspectionInfo.distance"),
        flex: 1,
        minWidth: 100,
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params) => {
          const timestamp = params.row.timestamp;
          return renderClickablecell(params.value, timestamp);
        },
      },
      {
        field: "timestamp",
        headerName: translationService.translate("ocrService.inspectionInfo.time"),
        flex: 1,
        minWidth: 100,
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params) => {
          const formattedTime = new Date(params.value).toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            hour12: false,
          });
          return renderClickablecell(formattedTime, params.value);
        },
      },
      {
        field: "text",
        headerName: translationService.translate("ocrService.inspectionInfo.detection"),
        renderCell: (params) => (
          <div style={{ whiteSpace: "normal", wordBreak: "break-word", lineHeight: "1.5" }}>
            {params.value}
          </div>
        ),
        flex: 6,
        minWidth: 400,
        sortable: false,
        disableColumnMenu: true,
      },
    ];
  },
  get extractedColumns(): GridColDef[] {
    return [
      {
        field: "distance",
        headerName: translationService.translate("ocrService.inspectionInfo.distance"),
        flex: 1,
        minWidth: 100,
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params) => {
          const timestamp = params.row.timestamp;
          return renderClickablecell(params.value, timestamp);
        },
      },
      {
        field: "timestamp",
        headerName: translationService.translate("ocrService.inspectionInfo.time"),
        flex: 1,
        minWidth: 100,
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params) => {
          const formattedTime = new Date(params.value).toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            hour12: false,
          });
          return renderClickablecell(formattedTime, params.value);
        },
      },
      { field: "main_Code", headerName: "MC", flex: 1, sortable: false, disableColumnMenu: true },
      { field: "c1", headerName: "C1", flex: 1, sortable: false, disableColumnMenu: true },
      { field: "c2", headerName: "C2", flex: 1, sortable: false, disableColumnMenu: true },
      { field: "q1", headerName: "Q1", flex: 1, sortable: false, disableColumnMenu: true },
      { field: "q2", headerName: "Q2", flex: 1, sortable: false, disableColumnMenu: true },
      { field: "postionFrom", headerName: translationService.translate("ocrService.inspectionInfo.positionFrom"), flex: 1.2, sortable: false, disableColumnMenu: true },
      { field: "positionTo", headerName: translationService.translate("ocrService.inspectionInfo.positionTo"), flex: 1.2, sortable: false, disableColumnMenu: true },
    ];
  },
  get combinedColumns(): GridColDef[] {
    const renderSpanCell = (value: string, top: boolean) => {
      return (
        <div style={{position: "relative", width: "27rem"}}>
          <div style={{position: "absolute", top:0, borderBottom: top ? "1px solid rgba(224, 224, 224, 1" : "none", width: "100%", paddingBottom: "50px"}}/>
          <div style={{overflowWrap: "break-word", wordBreak: "break-word", whiteSpace: "normal"}}>
            {value}
          </div>
        </div>
      )
    }
    
    return [
      {
        field: "distance",
        headerName: translationService.translate("ocrService.inspectionInfo.distance"),
        flex: 1,
        minWidth: 100,
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params) => {
          if (params.row.part === "bottom") return null;
          const timestamp = params.row.timestamp;
          return renderClickablecell(params.value, timestamp);
        },
        rowSpanValueGetter: () => null,
      },
      {
        field: "timestamp",
        headerName: translationService.translate("ocrService.inspectionInfo.time"),
        flex: 1,
        minWidth: 100,
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params) => {
          if (params.row.part === "bottom") return null;
          const formattedTime = new Date(params.value).toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            hour12: false,
          });
          return renderClickablecell(formattedTime, params.value);
        },
        rowSpanValueGetter: () => null,
      },
      { field: "main_Code", headerName: "MC", flex: 1, sortable: false, disableColumnMenu: true, renderCell: (params) => renderSpanCell(params.value, params.row.part === "top")},
      { field: "c1", headerName: "C1", flex: 1, sortable: false, disableColumnMenu: true, renderCell: (params) => params.row.part === "bottom" ? params.value : null },
      { field: "c2", headerName: "C2", flex: 1, sortable: false, disableColumnMenu: true, renderCell: (params) => params.row.part === "bottom" ? params.value : null },
      { field: "q1", headerName: "Q1", flex: 1, sortable: false, disableColumnMenu: true, renderCell: (params) => params.row.part === "bottom" ? params.value : null },
      { field: "q2", headerName: "Q2", flex: 1, sortable: false, disableColumnMenu: true, renderCell: (params) => params.row.part === "bottom" ? params.value : null },
      { field: "postionFrom", headerName: translationService.translate("ocrService.inspectionInfo.positionFrom"), flex: 1.2, sortable: false, disableColumnMenu: true, renderCell: (params) => params.row.part === "bottom" ? params.value : null },
      { field: "positionTo", headerName: translationService.translate("ocrService.inspectionInfo.positionTo"), flex: 1.2, sortable: false, disableColumnMenu: true, renderCell: (params) => params.row.part === "bottom" ? params.value : null },
    ];
  },
  get combinedColumnGroup(): GridColumnGroupingModel {
    return [
      {
        groupId: "detection",
        headerName: translationService.translate("ocrService.inspectionInfo.detection"),
        children: [ { field: "main_Code" }, { field: "c1" }, { field: "c2" }, { field: "q1" }, { field: "q2" }, { field: "postionFrom" }, { field: "positionTo" }]
      }
    ];
  },
  get columns(): GridColDef[]{
    if(self.view === SelectedView.Detected) return this.detectionColumns;
    if(self.view === SelectedView.Extracted) return this.extractedColumns;
    return this.combinedColumns;
  }
})).actions((self) => ({
  setDetections(data: typeof DetectionModel.Type[]) {
    self.detections.replace(data);
  },
  setView(view: ViewType) {
    self.view = view;
  },
  setSelectedIndex(index: number | null){
    self.selectedIndex = index;
  },
  reset() {
    self.detections.clear();
    self.view = SelectedView.Detected;
    self.selectedIndex = null;
  }
}));

export const inspectionInfoStorage = InspectionInfoStorageModel.create({
  detections: [],
  view: SelectedView.Detected,
});
